// type-only: these modules import this one, so a value import here would close a runtime cycle
import type GlobalDataCache from 'models/globalDataCache/globalDataCache';
import type ResourceLink from 'models/resourceLink';
import type ResourceList from 'models/resourceList';

/**
 * The system that physically holds a library category's content.
 * Mirrored by numeric value from the API (REDLAB.ISOPlanner.Contracts/Primitives/DocumentProvider.cs).
 */
export enum DocumentProviderType {
  SharePoint = 0,
  Virtual = 1,
  GoogleDrive = 2,
  Nextcloud = 3,
  Notion = 4,
  MicrosoftLoop = 5,
}

/**
 * What a provider can do, as data from the API rather than as a hardcoded check. Every
 * affordance in the library is gated on these flags, so adding a provider or switching a
 * feature on for one is a server change that needs no client release.
 */
export class DocumentProviderCapabilities {
  browse: boolean;

  linkExisting: boolean;

  upload: boolean;

  createFolder: boolean;

  download: boolean;

  embedPreview: boolean;

  /** Word, Excel and PowerPoint files are shown through the Office Online viewer on a copy the API parks behind a short-lived url. */
  officeOnlinePreview: boolean;

  webLink: boolean;

  thumbnail: boolean;

  versions: boolean;

  publish: boolean;

  approvals: boolean;

  readConfirmations: boolean;

  pagesKind: boolean;

  pageEdit: boolean;

  docxEdit: boolean;

  providerSearch: boolean;

  deleteContent: boolean;

  customList: boolean;

  wholeList: boolean;

  writeProbe: boolean;

  contentIngest: boolean;

  requiresConnection: boolean;

  /** A connection can run in Google Workspace mode: REDLAB's delegated account acts as the signed-in user. */
  supportsDomainDelegation: boolean;

  /** A connection can run in per-user mode: each user connects their own account once (Nextcloud OAuth2). */
  supportsUserDelegation: boolean;

  /** A Word document in the location can be converted to PDF there, so the report choices may offer PDF. */
  pdfConversion: boolean;

  constructor(all: boolean = false) {
    this.browse = all;
    this.linkExisting = all;
    this.upload = all;
    this.createFolder = all;
    this.download = all;
    this.embedPreview = all;
    this.officeOnlinePreview = false;
    this.webLink = all;
    this.thumbnail = all;
    this.versions = all;
    this.publish = all;
    this.approvals = all;
    this.readConfirmations = all;
    this.pagesKind = all;
    this.pageEdit = all;
    this.docxEdit = all;
    this.providerSearch = all;
    this.deleteContent = all;
    this.customList = all;
    this.wholeList = all;
    this.writeProbe = all;
    this.contentIngest = all;
    this.requiresConnection = false;
    this.supportsDomainDelegation = false;
    this.supportsUserDelegation = false;
    this.pdfConversion = all;
  }
}

export class DocumentProvider {
  providerType: DocumentProviderType;

  name: string;

  capabilities: DocumentProviderCapabilities;

  enabled: boolean;

  constructor() {
    this.providerType = DocumentProviderType.SharePoint;
    this.name = '';
    this.capabilities = new DocumentProviderCapabilities();
    this.enabled = false;
  }
}

/**
 * True when the category's content is served through the API broker instead of the browser's
 * own Graph calls. SharePoint and virtual categories keep their existing paths.
 *
 * Use this only to pick a transport. Whether the user may do something is answered by the
 * capabilities, never by comparing provider types.
 */
export function isBrokeredProvider(list: ResourceList | undefined): boolean {
  if (!list) return false;

  return list.providerType !== DocumentProviderType.SharePoint && list.providerType !== DocumentProviderType.Virtual;
}

/**
 * Translation keys cannot be built from variables, so the provider name resolves through an
 * explicit switch. The values are product names and stay untranslated in every language.
 */
export function getProviderName(providerType: DocumentProviderType): string {
  switch (providerType) {
    case DocumentProviderType.SharePoint:
      return 'SharePoint';
    case DocumentProviderType.Virtual:
      return 'ISOPlanner';
    case DocumentProviderType.GoogleDrive:
      return 'Google Drive';
    case DocumentProviderType.Nextcloud:
      return 'Nextcloud';
    case DocumentProviderType.Notion:
      return 'Notion';
    case DocumentProviderType.MicrosoftLoop:
      return 'Microsoft Loop';
    default:
      return '';
  }
}

/**
 * What the provider behind this category can do. This is the single accessor every affordance
 * gate goes through: it never returns undefined, so a gate cannot flip while the cache is
 * still loading, and a SharePoint category answers exactly as it did before providers existed.
 */
export function getListCapabilities(
  list: ResourceList | undefined,
  cache: GlobalDataCache,
): DocumentProviderCapabilities {
  const providerType = list?.providerType ?? DocumentProviderType.SharePoint;

  return cache.documentProviders.getCapabilities(providerType);
}

export function getLinkCapabilities(
  link: ResourceLink | undefined,
  cache: GlobalDataCache,
): DocumentProviderCapabilities {
  return getListCapabilities(link?.list, cache);
}

/** The providers whose content needs a stored tenant connection. */
export function providerRequiresConnection(providerType: DocumentProviderType): boolean {
  return (
    providerType === DocumentProviderType.GoogleDrive ||
    providerType === DocumentProviderType.Nextcloud ||
    providerType === DocumentProviderType.Notion
  );
}
